/* START_MODULE_CONTRACT
 * PURPOSE: React-контекст аутентификации: токены, user info, login/logout.
 * SCOPE: AuthProvider, useAuth; bootstrap из localStorage.
 * DEPENDS: M-FE-AUTH, userApi
 * LINKS: M-FE-AUTH, V-M-FE-AUTH
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * AuthProvider - провайдер контекста аутентификации
 * useAuth - хук доступа к контексту
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: добавлены контракты функций к существующим маркерам/блокам.
 * END_CHANGE_SUMMARY
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getUserInfo } from '../api/userApi';
import User from '../models/User';

const AuthContext = createContext(null);

/* START_METHOD_AuthProvider */
/* START_CONTRACT: AuthProvider
 * PURPOSE: Провайдер контекста аутентификации: bootstrap сессии, login/logout, хранение user.
 * INPUTS: { children: ReactNode }
 * OUTPUTS: { JSX.Element - AuthContext.Provider }
 * SIDE_EFFECTS: Чтение/запись localStorage (accessToken/refreshToken); запрос getUserInfo.
 * LINKS: M-FE-AUTH, V-M-FE-AUTH
 * END_CONTRACT: AuthProvider
 */
export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    /* START_BLOCK_BOOTSTRAP_SESSION */
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setLoading(false);
      return;
    }

    setIsAuthenticated(true);

    const controller = new AbortController();
    getUserInfo(controller.signal)
      .then(({ data }) => {
        if (!controller.signal.aborted) {
          setUser(User.fromResponse(data));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
    /* END_BLOCK_BOOTSTRAP_SESSION */
  }, []);

  /* START_METHOD_loginAction */
  /* START_CONTRACT: loginAction
   * PURPOSE: Сохранить полученные токены и пометить сессию как аутентифицированную.
   * INPUTS: { tokens: { accessToken: string, refreshToken: string } }
   * OUTPUTS: { void }
   * SIDE_EFFECTS: Запись localStorage; setIsAuthenticated(true); лог BLOCK_LOGIN_SUCCESS.
   * LINKS: V-M-FE-AUTH
   * END_CONTRACT: loginAction
   */
  const loginAction = useCallback((tokens) => {
    /* START_BLOCK_LOGIN */
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    setIsAuthenticated(true);
    console.info('[FeAuth][login][BLOCK_LOGIN_SUCCESS] tokens stored');
    /* END_BLOCK_LOGIN */
  }, []);
  /* END_METHOD_loginAction */

  /* START_METHOD_logout */
  /* START_CONTRACT: logout
   * PURPOSE: Очистить сессию: удалить токены, сбросить состояние аутентификации и user.
   * INPUTS: нет
   * OUTPUTS: { void }
   * SIDE_EFFECTS: Удаление из localStorage; setIsAuthenticated(false); лог BLOCK_LOGOUT.
   * LINKS: V-M-FE-AUTH
   * END_CONTRACT: logout
   */
  const logout = useCallback(() => {
    /* START_BLOCK_LOGOUT */
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setIsAuthenticated(false);
    setUser(null);
    console.info('[FeAuth][logout][BLOCK_LOGOUT] session cleared');
    /* END_BLOCK_LOGOUT */
  }, []);
  /* END_METHOD_logout */

  const value = useMemo(
    () => ({
      isAuthenticated,
      user,
      loading,
      login: loginAction,
      logout,
      setUser,
    }),
    [isAuthenticated, user, loading, loginAction, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
/* END_METHOD_AuthProvider */

/* START_METHOD_useAuth */
/* START_CONTRACT: useAuth
 * PURPOSE: Хук доступа к контексту аутентификации с проверкой провайдера.
 * INPUTS: нет
 * OUTPUTS: { AuthContextValue - { isAuthenticated, user, loading, login, logout, setUser } }
 * SIDE_EFFECTS: Бросает Error, если вызван вне AuthProvider.
 * LINKS: M-FE-AUTH
 * END_CONTRACT: useAuth
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
/* END_METHOD_useAuth */
